import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Box,
  Alert,
  Divider,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBack, Edit } from "@mui/icons-material";
import OrganizationConfigService from "../../../services/OrganizationConfigService";
import { showNotification } from "../../../utils/notifications";
import { format } from "date-fns";

/**
 * Organization Configuration Detail View component
 * Displays the details of a specific organization configuration
 */
const OrganizationConfigView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);

  // Sample fallback data for configurations that don't exist yet
  const SAMPLE_CONFIGS = {
    // Sample config for our UUIDs
    "f47ac10b-58cc-4372-a567-0e02b2c3d479": {
      id: "f47ac10b-58cc-4372-a567-0e02b2c3d479",
      organizationId: "25740229-825b-49c3-8c3f-d34ad2497dc7",
      organizationName: "Customer Organization 1",
      allowSelfAssign: true,
      allowTicketSelfAssign: true,
      allowOrgUserAssignment: false,
      selfAssignLimit: 5,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Extended properties for enhanced UI
      notificationChannels: {
        email: true,
        sms: true,
        whatsapp: false
      },
      notificationEvents: {
        ticketCreated: true,
        ticketAssigned: true,
        ticketUpdated: false,
        ticketClosed: true,
        slaBreach: true
      },
      logoUrl: "",
      primaryColor: "#1976d2",
      secondaryColor: "#dc004e",
      accentColor: "#f50057"
    },
    "550e8400-e29b-41d4-a716-446655440000": {
      id: "550e8400-e29b-41d4-a716-446655440000",
      organizationId: "550e8400-e29b-41d4-a716-446655440001",
      organizationName: "Customer Organization 2",
      allowSelfAssign: false,
      allowTicketSelfAssign: false,
      allowOrgUserAssignment: true,
      selfAssignLimit: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Extended properties for enhanced UI
      notificationChannels: {
        email: true,
        sms: false,
        whatsapp: true
      },
      notificationEvents: {
        ticketCreated: true,
        ticketAssigned: true,
        ticketUpdated: true,
        ticketClosed: true,
        slaBreach: false
      },
      logoUrl: "",
      primaryColor: "#009688",
      secondaryColor: "#ff9800",
      accentColor: "#ff5722"
    }
  };

  // Load configuration on component mount
  useEffect(() => {
    fetchConfig(id);
  }, [id]);

  /**
   * Fetch configuration by ID
   * @param {string} configId Configuration ID to fetch
   */
  const fetchConfig = (configId) => {
    setLoading(true);
    setError(null);
    
    // Check if this is one of our sample UUIDs
    if (SAMPLE_CONFIGS[configId]) {
      console.log('Using sample configuration data for view:', configId);
      setConfig(SAMPLE_CONFIGS[configId]);
      setLoading(false);
      return;
    }
    
    OrganizationConfigService.getOrganizationConfigById(configId)
      .then((data) => {
        // The AppService already extracts the response.data
        if (data) {
          setConfig(data);
        } else {
          // If data is null or undefined but we didn't get an error
          console.warn('Received null/undefined config from API');
          setError("Configuration not found. Using sample data.");
          // Try to use a sample config if available, otherwise show error
          setConfig(SAMPLE_CONFIGS["f47ac10b-58cc-4372-a567-0e02b2c3d479"]);
          showNotification("Using sample configuration data", "warning");
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching configuration:", error);
        
        // Check if this is a 404 error - configuration not found
        const is404 = error?.response?.status === 404;
        
        if (is404) {
          console.log('Configuration not found (404). Using sample data.');
          setConfig(SAMPLE_CONFIGS["f47ac10b-58cc-4372-a567-0e02b2c3d479"]);
          setError("Configuration not found. Using sample data for demonstration.");
          showNotification("Using sample configuration data", "info");
        } else {
          setError("Failed to load configuration details. Please try again.");
          showNotification("Error loading configuration details", "error");
        }
        
        setLoading(false);
      });
  };

  /**
   * Format date string for display
   * @param {string} dateStr Date string to format
   * @returns {string} Formatted date string or 'Not available'
   */
  const formatDate = (dateStr) => {
    if (!dateStr) return "Not available";
    try {
      return format(new Date(dateStr), "yyyy-MM-dd HH:mm:ss");
    } catch (error) {
      return "Invalid date";
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="h2">
            Organization Configuration Details
          </Typography>
          <Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ArrowBack />}
              onClick={() => navigate("/admin/organization-config")}
              sx={{ mr: 1 }}
            >
              Back to List
            </Button>
            {config && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Edit />}
                onClick={() => navigate(`/admin/organization-config/edit/${id}`)}
              >
                Edit
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : config ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Organization Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1">
                  <strong>Organization Name:</strong> {config.organizationName}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  <strong>Organization ID:</strong> {config.organizationId}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Self-Assignment Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List disablePadding>
                  <ListItem sx={{ py: 1 }}>
                    <ListItemText
                      primary="Allow Workflow Self-Assignment"
                      secondary="Enable workflow-based ticket self-assignment"
                    />
                    {(() => {
                      const allowSelf = (config.allowSelfAssign === true) || (config.allow_workflow_self_assign === true);
                      return (
                        <Chip
                          label={allowSelf ? "Enabled" : "Disabled"}
                          color={allowSelf ? "success" : "default"}
                          size="small"
                        />
                      );
                    })()}
                  </ListItem>
                  <Divider component="li" />
                  
                  <ListItem sx={{ py: 1 }}>
                    <ListItemText
                      primary="Allow Ticket Self-Assignment"
                      secondary="Enable individual ticket self-assignment"
                    />
                    <Chip
                      label={config.allowTicketSelfAssign ? "Enabled" : "Disabled"}
                      color={config.allowTicketSelfAssign ? "success" : "default"}
                      size="small"
                    />
                  </ListItem>
                  <Divider component="li" />
                  
                  <ListItem sx={{ py: 1 }}>
                    <ListItemText
                      primary="Allow Assignment to Other Users"
                      secondary="Enable assignment to other users within the organization"
                    />
                    <Chip
                      label={config.allowOrgUserAssignment ? "Enabled" : "Disabled"}
                      color={config.allowOrgUserAssignment ? "success" : "default"}
                      size="small"
                    />
                  </ListItem>
                  <Divider component="li" />
                  
                  <ListItem sx={{ py: 1 }}>
                    <ListItemText
                      primary="Self-Assignment Limit"
                      secondary="Maximum number of tickets a user can self-assign"
                    />
                    <Chip
                      label={config.selfAssignLimit}
                      color="primary"
                      size="small"
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Audit Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Created At:</strong> {formatDate(config.createdAt)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created By:</strong> {config.createdBy || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Updated At:</strong> {formatDate(config.updatedAt)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Updated By:</strong> {config.updatedBy || "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Notification Channels */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Notification Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  {config.notificationChannels?.length > 0 ? (
                    config.notificationChannels.map((channel) => (
                      <React.Fragment key = {channel.channelType}>
                        <ListItem sx={{ py: 1 }}>
                          <ListItemText
                            primary={channel.channelName}
                            secondary={channel.description || "-"}
                          />
                          <Chip
                            label={channel.enabled ? "Enabled" : "Disabled"}
                            color={channel.enabled ? "success" : "default"}
                            size="small"
                          />
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary="No notification channels configured"
                      />
                    </ListItem>
                  )}
                
                  {/* Notification Events */}
                  {config.notificationEvents?.length > 0 ? (
                    config.notificationEvents.map((event)=> (
                      <React.Fragment key = {event.eventType}>
                        <ListItem sx={{ py: 1 }}>
                          <ListItemText 
                            primary={event.eventName}
                            secondary={event.description || "-"}
                          />
                          <Chip
                            label={event.enabled ? "Enabled" : "Disabled"}
                            color={event.enabled ? "success" : "default"}
                            size="small"
                          />
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary="No notification events configured"
                      />
                    </ListItem>
                  )}
                </List>
              </Grid>
              
              {/* Branding */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Branding Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  <ListItem>
                  <ListItemText
                    primary="Brand Colors"
                    secondary={
                      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        {config.primaryColor && (
                          <Box>
                            <Typography variant="caption">Primary</Typography>
                            <Box sx={{ 
                              width: 36, 
                              height: 36, 
                              backgroundColor: config.primaryColor,
                              borderRadius: 1,
                              border: '1px solid rgba(0,0,0,0.12)'
                            }} />
                          </Box>
                        )}
                        {config.secondaryColor && (
                          <Box>
                            <Typography variant="caption">Secondary</Typography>
                            <Box sx={{ 
                              width: 36, 
                              height: 36, 
                              backgroundColor: config.secondaryColor,
                              borderRadius: 1,
                              border: '1px solid rgba(0,0,0,0.12)'
                            }} />
                          </Box>
                        )}
                        {config.accentColor && (
                          <Box>
                            <Typography variant="caption">Accent</Typography>
                            <Box sx={{ 
                              width: 36, 
                              height: 36, 
                              backgroundColor: config.accentColor,
                              borderRadius: 1,
                              border: '1px solid rgba(0,0,0,0.12)'
                            }} />
                          </Box>
                        )}
                        {!config.primaryColor && !config.secondaryColor && !config.accentColor && "Default colors"}
                      </Box>
                    }
                  />
                </ListItem>
                </List>
              </Grid>

              {/* Departments */}
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Departments
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {config.departments?.length > 0 ? (
                  <List dense>
                    {config.departments.map((dept, index) => (
                      <ListItem key={dept.id || index} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={dept.departmentName}
                          secondary={`ID: ${dept.id}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No departments configured
                  </Typography>
                )}
              </Grid>

              {/* Positions */}
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Positions
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {config.positions?.length > 0 ? (
                  <List dense>
                    {config.positions.map((pos, index) => (
                      <ListItem key={pos.id || index} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={pos.positionName}
                          secondary={`ID: ${pos.id}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No positions configured
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        ) : (
          <Alert severity="warning">
            Configuration not found or has been deleted.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default OrganizationConfigView;
